import { DetailsItem } from 'components/secondaryMarket/shared/SidePanel/DetailsItem';
import { DetailsItemValue } from 'components/secondaryMarket/shared/SidePanel/DetailsItemValue';
import { LabelDetailsItem } from 'components/secondaryMarket/shared/SidePanel/LabelDetailsItem';
import { useTradeWayLabel } from 'components/secondaryMarket/shared/status/tradeWay';
import { ListGroup } from 'components/shared/ListGroup';
import { useSPIntl } from 'hooks/useIntl';
import { FC } from 'react';
import type { SecondaryPriceType, SecondaryQuotationType } from 'state/secondaryMarket/productSearch';
import type { TradeWay } from 'state/secondaryMarket/secondaryMarket.model';

export type QuotedDetailsProps = Readonly<{
  className?: string;
  quoteId?: string;
  settlementDate?: string;
  tradeWay?: TradeWay;
  upfront?: number;
  upfrontAdjusted?: number;
  salesMargin?: number;
  salesMarginAdjustment?: number;
  tradingPrice?: number;
  accruedInterest?: number;
  tradingPriceAdjustment?: number;
  quantity?: number;
  settlementPrice?: number;
  effectiveCashAmount?: number;
  priceType: SecondaryPriceType;
  currency: string;
  tradeDate?: string;
  quotationType?: SecondaryQuotationType;
}>;

export const QuotedDetails: FC<QuotedDetailsProps> = ({
  quoteId,
  settlementDate,
  tradeWay,
  upfront,
  upfrontAdjusted,
  salesMargin,
  salesMarginAdjustment,
  tradingPrice,
  tradingPriceAdjustment,
  priceType,
  className,
  effectiveCashAmount,
  currency,
  quantity,
  tradeDate,
  accruedInterest,
  quotationType,
}) => {
  const { formatDisplayedDate, formatNumber } = useSPIntl();
  const tradeWayLabel = useTradeWayLabel({ way: tradeWay });
  return (
    <ListGroup className={className}>
      {quoteId !== undefined && (
        <LabelDetailsItem label="secondaryMarket.sidePanel.label.quoteId" value={quoteId} e2e="quoteId" />
      )}
      {tradeWay !== undefined && (
        <DetailsItem label="secondaryMarket.sidePanel.label.tradeWay" e2e="tradeWay">
          {tradeWayLabel}
        </DetailsItem>
      )}
      {quantity !== undefined && (
        <DetailsItem label="secondaryMarket.sidePanel.label.quantity" e2e="quantity">
          {quantity}
        </DetailsItem>
      )}
      {tradeDate && (
        <DetailsItem label="secondaryMarket.sidePanel.label.tradeDate" e2e="tradeDate">
          {formatDisplayedDate(tradeDate)}
        </DetailsItem>
      )}
      <DetailsItemValue
        label="secondaryMarket.sidePanel.label.upfront"
        e2e="upfront"
        value={upfront}
        priceType={priceType}
        currency={currency}
        alreadyFormatted
      />
      <DetailsItemValue
        label="secondaryMarket.sidePanel.label.salesMargin"
        e2e="salesMargin"
        value={salesMargin}
        priceType={priceType}
        currency={currency}
        alreadyFormatted
      />

      <DetailsItemValue
        label="secondaryMarket.sidePanel.label.tradingPrice"
        e2e="tradingPrice"
        value={tradingPrice}
        priceType={priceType}
        currency={currency}
        alreadyFormatted
      />
      <DetailsItemValue
        label="secondaryMarket.sidePanel.label.upfrontAdjustment"
        e2e="upfront"
        value={upfrontAdjusted}
        priceType={priceType}
        currency={currency}
      />
      <DetailsItemValue
        label="secondaryMarket.sidePanel.label.salesMarginAdjustment"
        e2e="salesMarginAdjustment"
        value={salesMarginAdjustment}
        priceType={priceType}
        currency={currency}
      />
      <DetailsItemValue
        label="secondaryMarket.sidePanel.label.tradingPriceAdjustment"
        e2e="tradingPriceAdjustment"
        value={tradingPriceAdjustment}
        priceType={priceType}
        currency={currency}
      />
      {quotationType === 'Clean' && accruedInterest !== undefined && (
        <DetailsItemValue
          label="secondaryMarket.sidePanel.label.accruedInterest"
          e2e="accruedInterest"
          value={accruedInterest}
          priceType={priceType}
          currency={currency}
          alreadyFormatted
        />
      )}
      {settlementDate !== undefined && (
        <DetailsItem label="secondaryMarket.sidePanel.label.settlementDate" e2e="settlementDate">
          {formatDisplayedDate(settlementDate)}
        </DetailsItem>
      )}
      {effectiveCashAmount !== undefined && (
        <DetailsItem label="secondaryMarket.sidePanel.label.settlementCashAmount" e2e="settlementDate">
          {formatNumber(effectiveCashAmount, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}{' '}
          <span className="ms-1">{currency}</span>
        </DetailsItem>
      )}
    </ListGroup>
  );
};
